import type { PrinterConfig, PrinterDevice, PrinterStatusResponse } from '@abcpay/shared'
import { DEFAULT_PRINTER_CONFIG } from '@abcpay/shared'
import { bridge, BridgeNotAvailableError } from '../bridge'
import {
  loadPrinterSettings as loadFromStorage,
  savePrinterSettings as saveToStorage,
} from '../storage'

// 사용 가능한 프린터 목록 가져오기
export async function getAvailablePrinters(): Promise<PrinterDevice[]> {
  try {
    // Electron에서만 하드웨어 프린터 목록 조회 가능
    if (!bridge.isElectron) {
      return []
    }
    const result = await bridge.listPrinters()
    return result.devices
  } catch (error) {
    if (error instanceof BridgeNotAvailableError) {
      return []
    }
    console.error('프린터 목록 조회 실패:', error)
    return []
  }
}

// 프린터 설정 로드
export async function loadPrinterSettings(): Promise<PrinterConfig> {
  try {
    // Electron에서는 Bridge를 통해 파일 시스템에서 로드
    if (bridge.isElectron) {
      const result = await bridge.getPrinterSettings()
      return result.config
    }
    // 브라우저에서는 localStorage에서 로드
    return await loadFromStorage()
  } catch (error) {
    console.error('프린터 설정 로드 실패:', error)
    return DEFAULT_PRINTER_CONFIG
  }
}

// 프린터 설정 저장
export async function savePrinterSettings(config: PrinterConfig): Promise<PrinterStatusResponse> {
  try {
    // Electron에서는 Bridge를 통해 파일 시스템에 저장
    if (bridge.isElectron) {
      const result = await bridge.savePrinterSettings({ config })
      return {
        success: result.success,
        message: result.message,
      }
    }
    // 브라우저에서는 localStorage에 저장
    await saveToStorage(config)
    return {
      success: true,
      message: '프린터 설정이 저장되었습니다.',
    }
  } catch (error) {
    return {
      success: false,
      message: '설정 저장 실패',
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

// 프린터 테스트
export async function testPrinter(): Promise<PrinterStatusResponse> {
  try {
    if (!bridge.isElectron) {
      return {
        success: false,
        message: '프린터 테스트는 POS 앱에서만 가능합니다.',
        error: 'BROWSER_MODE'
      }
    }
    const result = await bridge.testPrinter()
    return result
  } catch (error) {
    if (error instanceof BridgeNotAvailableError) {
      return {
        success: false,
        message: '프린터 테스트는 POS 앱에서만 가능합니다.',
        error: 'BROWSER_MODE'
      }
    }
    return {
      success: false,
      message: '테스트 출력 실패',
      error: error instanceof Error ? error.message : String(error)
    }
  }
}

// 프린터 기능 사용 가능 여부
export function isPrinterSettingsAvailable(): boolean {
  return bridge.isElectron
}
